import i18n from "i18next";
import ar from "@/i18n/locales/ar.json";
import en from "@/i18n/locales/en.json";
import { DEFAULT_LANGUAGE, type SupportedLanguage } from "@/lib/site";

/**
 * i18next setup, ported from apps/web/src/i18n (same resources/keys pattern) — but deliberately
 * *not* wired through react-i18next's <I18nextProvider>/useTranslation() global-language state.
 *
 * Why: vite-react-ssg prerenders every locale route (see src/app/routes.tsx) concurrently in the
 * same Node process during `build`. useTranslation() reads the *shared* i18n.language, so two
 * routes rendering at once (e.g. /ar and /en) would race and could bleed one locale's strings
 * into the other's static HTML. `i18n.getFixedT(lang)` instead returns a translator bound to a
 * single, explicit language with no shared mutable state — safe for concurrent SSG renders, and
 * still just as safe for client-side hydration (see src/pages/HomePage.tsx).
 */
void i18n.init({
  resources: {
    ar: { translation: ar },
    en: { translation: en },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  // Resources are bundled up-front (no i18next-http-backend), so init completes synchronously —
  // required so getFixedT() below works immediately at both build time and first paint.
  initImmediate: false,
});

export function t(lang: SupportedLanguage) {
  return i18n.getFixedT(lang);
}

export default i18n;
